import React from 'react'
import styles from '../GlobalSSOBanner.module.css'

export type SlideDismissProps = React.PropsWithChildren<{
  /** Prop to initiate the slide dismissal animation */
  hidden?: boolean
}>

const ANIMATION_DURATION = 300 // (ms) var(--base-duration-300)

export function SlideDismiss({children, hidden}: SlideDismissProps) {
  const [visibilityState, setVisibilityState] = React.useState<'visible' | 'hidden' | 'collapsing'>('visible')
  const outerWrapperRef = React.useRef<HTMLDivElement>(null)
  const bannerHeight = React.useRef<number | undefined>(undefined)

  React.useEffect(() => {
    if (hidden && visibilityState === 'visible') {
      setVisibilityState('collapsing')

      const wrapper = outerWrapperRef.current

      if (wrapper) {
        const height = wrapper.getBoundingClientRect().height
        bannerHeight.current = height
        // Set the height to the current height just before animating so CSS can animate it
        wrapper.style.height = `${height}px`
      }
    } else if (visibilityState === 'collapsing') {
      // Once we start collapsing, set a timer to remove the banner from the DOM
      const timer = setTimeout(() => {
        setVisibilityState('hidden')
      }, ANIMATION_DURATION)

      return () => {
        clearTimeout(timer)
      }
    }
  }, [hidden, visibilityState])

  if (visibilityState === 'hidden') {
    return null
  }

  return (
    <div
      ref={outerWrapperRef}
      style={{
        height: visibilityState === 'visible' ? bannerHeight.current ?? 'auto' : 0,
      }}
      className={styles.bannerOuterWrapper}
    >
      <div
        className={styles.bannerInnerWrapper}
        style={{
          transform: visibilityState === 'visible' ? undefined : `translateY(-${bannerHeight.current ?? 0}px)`,
        }}
      >
        {children}
      </div>
    </div>
  )
}

try{ ANIMATION_DURATION.displayName ||= 'ANIMATION_DURATION' } catch {}
try{ SlideDismiss.displayName ||= 'SlideDismiss' } catch {}